import * as _ from 'lodash'

export interface IDataSourceRequestInit extends RequestInit {
  recordsPath: string
  visualizationPath: string
  updateRequestOnSync?: (requestInit: IDataSourceRequestInit) => IDataSourceRequestInit | Promise<IDataSourceRequestInit>
}

type DataSourceListener<T, U> = (syncObj: { records: T[], visualization: U, initDataSourceId: string }) => void

export class DataSource<T, U> {
  private static DataSourceIdCounter = 1000
  private static getDataSourceId(): string {
    DataSource.DataSourceIdCounter++

    return `#${DataSource.DataSourceIdCounter}`
  }

  protected dataFetch: Promise<void>
  protected records: T[]
  protected visualization: U

  private listeners: Array<DataSourceListener<T, U>> = []
  private dataSourceDependencies: Array<DataSource<any, any>> = []
  private debounceSync = _.debounce((initDataSourceId: string) => this.updateListeners(initDataSourceId), 25)
  private dataSourceId = DataSource.getDataSourceId()

  constructor(
    private requestUrl: string,
    private requestInit: IDataSourceRequestInit
  ) { /**/ }

  /**
   * Add listener to DataSource
   * @param listener
   * @param immediate
   */
  onSync(listener: (DataSourceListener<T, U>), immediate = true) {
    this.listeners.push(listener)

    if (immediate && Array.isArray(this.records)) {
      listener({
        records: this.records,
        visualization: this.visualization,
        initDataSourceId: this.dataSourceId
      })
    }

    return () => {
      const listenerIndex = this.listeners.indexOf(listener)

      if (listenerIndex >= 0) {
        this.listeners.splice(listenerIndex, 1)
      }
    }
  }

  /**
   * Loads data and invokes listeners
   * @param refreshData force HttpRequest
   * @param initDataSourceId defaults to instance's Id
   */
  sync(refreshData = false, initDataSourceId?: string): this {
    const isCircularCall = initDataSourceId === this.dataSourceId

    //Don't cause a circular invokation
    if (isCircularCall) { return this }

    const properInitDataSourceId = initDataSourceId ? initDataSourceId : this.dataSourceId

    this.loadData(refreshData).then(() => this.debounceSync(properInitDataSourceId))

    return this
  }

  /**
   * Triggers a sync on dataSources every time the instance
   * completes a sync
   * @param dataSources
   */
  addDataSourceListener(...dataSources: Array<DataSource<any, any>>): this {
    this.dataSourceDependencies = this.dataSourceDependencies.concat(dataSources)

    return this
  }

  syncDependencies(): this {
    //Invoke triggerRefreshOnSync DataSources
    this.dataSourceDependencies.forEach((dataSourceListener) => {
      dataSourceListener.sync(true, this.dataSourceId)
    })

    return this
  }

  sort(compareFn: ((a: T, b: T) => number)): this {
    this.loadData().then(() => {
      this.records.sort(compareFn)

      this.debounceSync(this.dataSourceId)
    })

    return this
  }

  /**
   * Invoke Listeners
   * @param initDataSourceId DataSource's Id that started sync
   */
  protected updateListeners(initDataSourceId: string) {
    //Invoke OnSync listeners
    this.listeners.forEach((listener) => {
      if (typeof listener === 'function') {
        listener({
          records: this.records,
          visualization: this.visualization,
          initDataSourceId: this.dataSourceId
        })
      }
    })
  }

  /**
   * Load Data from endpoint, if loaded returns cached result
   * @param refreshData forces HttpRequest
   */
  protected loadData(refreshData = false) {
    if (refreshData || !this.dataFetch) {
      const { updateRequestOnSync } = this.requestInit

      if (updateRequestOnSync) {
        const requestInitCopy = _.cloneDeep(this.requestInit)
        const updateCall = updateRequestOnSync(requestInitCopy)

        if ('then' in updateCall) {
          return updateCall.then((requestInit) => {
            this.requestInit = requestInit
            return this.setDataFetch()
          })
        } else {
          this.requestInit = updateCall
        }
      }

      this.setDataFetch()
    }

    return this.dataFetch
  }

  protected setDataFetch() {
    this.dataFetch = fetch(this.requestUrl, this.requestInit)
      .then((res) => res.json())
      .then((jsonRes) => {
        this.records = _.get(jsonRes, this.requestInit.recordsPath)
        this.visualization = _.get(jsonRes, this.requestInit.visualizationPath)
      })
      .catch((reason) => {
        console.log('something went wrong with response from endpoint');
      })

    return this.dataFetch
  }
}
